// Das Kachelschieberätsel nach einer Ausbaustufe.
//
// **Es entscheidet nichts.** Die Stufe ist bezahlt und gebaut, bevor das Rätsel
// erscheint; Lösen gibt eine Meldung, Abbrechen kostet nichts. Genau darum gibt
// es hier keine Autoritätsfrage: online könnte der Server ein Schiebepuzzle nur
// prüfen, indem er die Zugfolge nachspielt — und die müsste der Client melden.
// Das wäre eine weitere Client-Nachricht und damit ein Bruch von FG-32, für eine
// Handlung, die pro Spielstand sechsmal vorkommt (`NET-001`).
//
// Das Mischen ist **deterministisch** aus der Ausbaustufe: dasselbe Rätsel nach
// jedem Neuladen. Und es entsteht aus gültigen Zügen rückwärts — ein zufällig
// vertauschtes Feld wäre in der Hälfte der Fälle unlösbar.

import { hash3, rngFrom } from '../core/rng';

export const PUZZLE_SIZE = 3;
const TILES = PUZZLE_SIZE * PUZZLE_SIZE;
const HOLE = TILES - 1;
const PUZZLE_SALT = 0x9E37;
const SHUFFLE_MOVES = 60;

/** Die vier Nachbarn eines Feldes, als Index. */
function neighbours(index: number): number[] {
  const x = index % PUZZLE_SIZE;
  const y = Math.floor(index / PUZZLE_SIZE);
  const out: number[] = [];
  if (x > 0) out.push(index - 1);
  if (x < PUZZLE_SIZE - 1) out.push(index + 1);
  if (y > 0) out.push(index - PUZZLE_SIZE);
  if (y < PUZZLE_SIZE - 1) out.push(index + PUZZLE_SIZE);
  return out;
}

/**
 * Ein gemischtes, **lösbares** Feld zur Ausbaustufe.
 *
 * Gemischt wird durch gültige Züge vom gelösten Feld aus — ein zufällig
 * vertauschtes Feld wäre in der Hälfte der Fälle unlösbar, und das merkte der
 * Spieler erst nach Minuten.
 */
export function shuffled(stage: number, seed: number): number[] {
  const tiles = Array.from({ length: TILES }, (_, i) => i);
  const rng = rngFrom(hash3(stage, PUZZLE_SALT, seed));
  let hole = HOLE;

  for (let step = 0; step < SHUFFLE_MOVES; step++) {
    const options = neighbours(hole);
    const pick = options[Math.floor(rng() * options.length)];
    tiles[hole] = tiles[pick];
    tiles[pick] = HOLE;
    hole = pick;
  }
  return tiles;
}

export const solved = (tiles: number[]): boolean => tiles.every((tile, i) => tile === i);

/**
 * Eine Kachel schieben. Gibt das neue Feld zurück, oder `null` bei einem
 * ungültigen Zug — der Aufrufer muss nicht selbst prüfen, ob es passt.
 */
export function slide(tiles: number[], index: number): number[] | null {
  const hole = tiles.indexOf(HOLE);
  if (!neighbours(index).includes(hole)) return null;
  const next = [...tiles];
  next[hole] = next[index];
  next[index] = HOLE;
  return next;
}

export const isHole = (tile: number): boolean => tile === HOLE;
